import type { FilterOptions } from '../data/types'
import type { FilterState, When } from '../data/filterState'
import { toggleInList } from '../data/filterState'
import styles from './FilterPanel.module.css'

const REG_DOT: Record<string, string> = { open: '#1f9d4c', soon: '#2f6fdb', full: '#8a8e95' }

/** Registration + Racing type + Vehicle type checkbox groups, shared verbatim between the desktop
 *  rail (Desktop.dc.html's `groups` loop) and the mobile sheet - one consistent checkbox treatment
 *  on both breakpoints (the mockup's mobile sheet uses pill chips for Racing type instead; kept as
 *  checkboxes here for one shared component rather than two near-duplicate renderings of the same
 *  state). Vehicle type (the real vehicletypecodes.typeName values, e.g. "1/10th Electric Truck")
 *  wasn't in the mockup at all - added as a third checkbox group in the same style, hidden
 *  entirely when the current result set has none. The When chip row (mobile-only, since desktop's
 *  When lives in the hero's date select) and the Series select render alongside this, not inside
 *  it - see DesktopFilterRail/MobileFilterSheet. */
export function FilterPanel({ filters, selected, onChange }: { filters: FilterOptions; selected: FilterState; onChange: (next: FilterState) => void }) {
  return (
    <>
      <fieldset className={styles.group}>
        <legend className={styles.groupLegend}>Registration</legend>
        {filters.registration.map((opt) => (
          <label key={opt.value} className={styles.checkboxRow}>
            <input
              type="checkbox"
              className={styles.checkbox}
              checked={selected.regState.includes(opt.value)}
              onChange={() => onChange({ ...selected, regState: toggleInList(selected.regState, opt.value) })}
            />
            <span className={styles.checkboxDot} style={{ background: REG_DOT[opt.value] }} />
            <span className={styles.checkboxLabel}>{opt.label}</span>
            <span className={styles.checkboxCount}>{opt.count}</span>
          </label>
        ))}
      </fieldset>

      <fieldset className={styles.group}>
        <legend className={styles.groupLegend}>Racing type</legend>
        {filters.raceType.map((opt) => (
          <label key={opt.value} className={styles.checkboxRow}>
            <input
              type="checkbox"
              className={styles.checkbox}
              checked={selected.raceType.includes(opt.value)}
              onChange={() => onChange({ ...selected, raceType: toggleInList(selected.raceType, opt.value) })}
            />
            <span className={styles.checkboxLabel}>{opt.label}</span>
            <span className={styles.checkboxCount}>{opt.count}</span>
          </label>
        ))}
      </fieldset>

      {filters.vehicleType.length ? (
        <fieldset className={styles.group}>
          <legend className={styles.groupLegend}>Vehicle type</legend>
          {filters.vehicleType.map((opt) => (
            <label key={opt.value} className={styles.checkboxRow}>
              <input
                type="checkbox"
                className={styles.checkbox}
                checked={selected.vehicleType.includes(opt.value)}
                onChange={() => onChange({ ...selected, vehicleType: toggleInList(selected.vehicleType, opt.value) })}
              />
              <span className={styles.checkboxLabel}>{opt.label}</span>
              <span className={styles.checkboxCount}>{opt.count}</span>
            </label>
          ))}
        </fieldset>
      ) : null}
    </>
  )
}

export function SeriesSelect({ filters, selected, onChange }: { filters: FilterOptions; selected: FilterState; onChange: (next: FilterState) => void }) {
  return (
    <label className={styles.group}>
      <span className={styles.groupLegend}>Series</span>
      <select
        className={styles.seriesSelect}
        value={selected.seriesTrackId ?? ''}
        onChange={(e) => onChange({ ...selected, seriesTrackId: e.target.value ? Number(e.target.value) : null })}
      >
        <option value="">All series</option>
        {filters.series.map((s) => (
          <option key={s.trackId} value={s.trackId}>
            {s.name} ({s.count})
          </option>
        ))}
      </select>
    </label>
  )
}

const WHEN_OPTIONS: Array<{ value: When; label: string }> = [
  { value: 'any', label: 'Any time' },
  { value: 'weekend', label: 'This weekend' },
  { value: '30days', label: 'Next 30 days' },
]

/** Mobile-only chip row - desktop's equivalent control is the hero's date <select>. */
export function WhenChips({ selected, onChange }: { selected: FilterState; onChange: (next: FilterState) => void }) {
  return (
    <fieldset className={styles.group}>
      <legend className={styles.groupLegend}>When</legend>
      <div className={styles.chipRow}>
        {WHEN_OPTIONS.map((o) => (
          <button
            key={o.value}
            type="button"
            aria-pressed={selected.when === o.value}
            className={`${styles.chip} ${selected.when === o.value ? styles.chipActive : ''}`}
            onClick={() => onChange({ ...selected, when: o.value })}
          >
            {o.label}
          </button>
        ))}
      </div>
    </fieldset>
  )
}
